import React from 'react';

const Refund: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Refund & Cancellation Policy</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Last updated: December 2024</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Policy Overview</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              At Lunexa, we are committed to delivering exceptional IT automation, development, and AI solutions that meet your business needs. 
              This comprehensive refund and cancellation policy outlines the terms and conditions under which refunds may be provided for our services.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We understand that business requirements can change, and we strive to be fair and transparent in our refund processes while 
              protecting the interests of both our clients and our business operations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. Refund Eligibility Criteria</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Due to the custom nature of our technology services, refunds are evaluated based on specific criteria. We offer refunds under the following conditions:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Eligible for Full Refund:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Service was not delivered as specified in the signed agreement</li>
              <li>Project cancellation within 48 hours of contract execution</li>
              <li>Failure to meet agreed-upon milestones due to Lunexa's fault</li>
              <li>Technical issues on our end that prevent service delivery</li>
              <li>Breach of service level agreements by Lunexa</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Eligible for Partial Refund:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Project scope changes initiated by the client</li>
              <li>Cancellation after work has commenced but before completion</li>
              <li>Service quality issues that can be partially remedied</li>
              <li>Delays caused by external factors beyond our control</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Refund Timeframes and Amounts</h2>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Project Cancellation Refunds</h3>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li><strong>Within 48 hours:</strong> 100% refund (no work commenced)</li>
                <li><strong>Within 7 days:</strong> 75% refund (minus documented work completed)</li>
                <li><strong>7-30 days:</strong> 50% refund (minus work completed and resources allocated)</li>
                <li><strong>After 30 days:</strong> Refund calculated based on remaining deliverables</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Service Performance Issues</h3>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li><strong>Technical failures:</strong> Full refund for affected service components</li>
                <li><strong>Delayed delivery:</strong> 10-25% refund or equivalent service credit</li>
                <li><strong>Quality issues:</strong> Free rework or 15-50% partial refund</li>
                <li><strong>Scope misalignment:</strong> Adjustment or partial refund based on delivered value</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg mb-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Service-Specific Refund Terms</h3>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li><strong>IT Automation:</strong> Refund based on automation goals achieved</li>
                <li><strong>Development Projects:</strong> Milestone-based refund calculation</li>
                <li><strong>AI Solutions:</strong> Performance metrics-based refund assessment</li>
                <li><strong>Consulting Services:</strong> Hourly rate refund for undelivered sessions</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Non-Refundable Services and Costs</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">The following services and costs are generally non-refundable:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Completed Services:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Completed and approved consulting sessions</li>
              <li>Delivered and client-accepted work products</li>
              <li>Successfully deployed solutions and applications</li>
              <li>Training sessions that have been conducted</li>
              <li>Research and analysis reports that have been delivered</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Third-Party and External Costs:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Third-party software licenses and subscriptions</li>
              <li>Cloud hosting and infrastructure costs</li>
              <li>Domain registrations and SSL certificates</li>
              <li>External API usage fees</li>
              <li>Marketplace listing fees and commissions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Time-Based Exclusions:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Services actively used for more than 60 days</li>
              <li>Projects where final deliverables were accepted more than 30 days ago</li>
              <li>Ongoing maintenance and support services</li>
              <li>Custom development work in production use</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Refund Request Process</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">To request a refund, please follow these steps:</p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg mb-4 border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Step-by-Step Process:</h3>
              <ol className="list-decimal pl-6 text-gray-600 dark:text-gray-400 space-y-2">
                <li><strong>Initial Contact:</strong> Email our support team at support@lunexa.dpdns.org</li>
                <li><strong>Documentation:</strong> Provide project details, contract information, and specific reason for refund</li>
                <li><strong>Supporting Evidence:</strong> Include any relevant documentation or communication records</li>
                <li><strong>Review Period:</strong> Allow 5-7 business days for our team to review your request</li>
                <li><strong>Decision Notification:</strong> Receive written notification of approval, partial approval, or denial</li>
                <li><strong>Processing:</strong> If approved, refund will be processed within 10-15 business days</li>
              </ol>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Required Information:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Project or service agreement reference number</li>
              <li>Detailed explanation of the refund request reason</li>
              <li>Timeline of events leading to the refund request</li>
              <li>Any communication records with our team</li>
              <li>Preferred refund method (original payment method recommended)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Partial Refund Calculations</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              When work has been partially completed, refunds are calculated using a fair and transparent methodology:
            </p>
            
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg mb-4 border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Calculation Factors:</h3>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li><strong>Completion Percentage:</strong> Measurable progress on deliverables</li>
                <li><strong>Resource Allocation:</strong> Time and personnel already committed</li>
                <li><strong>Third-Party Costs:</strong> Non-recoverable external expenses</li>
                <li><strong>Setup and Planning:</strong> Initial project investment and preparation</li>
                <li><strong>Intellectual Property:</strong> Custom solutions and proprietary work delivered</li>
              </ul>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Formula:</strong> Refund Amount = Total Paid - (Completed Work Value + Non-Recoverable Costs + Administrative Fee)
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Dispute Resolution and Appeals</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you disagree with our refund decision, we provide a fair appeals process:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Appeals Process:</h3>
            <ol className="list-decimal pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Submit a written appeal within 30 days of the initial decision</li>
              <li>Provide additional evidence or clarification for your case</li>
              <li>Request review by senior management or an independent mediator</li>
              <li>Participate in good faith discussions to reach a resolution</li>
              <li>Receive final decision within 15 business days</li>
            </ol>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We are committed to finding fair and equitable solutions that consider both client satisfaction and business sustainability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. Refund Processing and Payment Methods</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Approved refunds will be processed to the original payment method whenever possible:
            </p>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg mb-4 border border-yellow-200 dark:border-yellow-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Processing Timeframes:</h3>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li><strong>Credit/Debit Cards:</strong> 5-10 business days</li>
                <li><strong>Bank Wire Transfers:</strong> 7-14 business days</li>
                <li><strong>PayPal:</strong> 3-5 business days</li>
                <li><strong>Digital Wallets:</strong> 3-7 business days</li>
                <li><strong>Cryptocurrency:</strong> 1-3 business days (subject to network conditions)</li>
              </ul>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              <strong>Note:</strong> Processing times may vary based on your financial institution's policies. International transfers may take additional time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Service Credits and Alternatives</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              In some cases, we may offer alternatives to monetary refunds:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li><strong>Service Credits:</strong> Future service credits with extended validity</li>
              <li><strong>Service Upgrades:</strong> Enhanced features or additional services at no cost</li>
              <li><strong>Extended Support:</strong> Complimentary maintenance and support periods</li>
              <li><strong>Re-work Options:</strong> Revised deliverables to meet original requirements</li>
              <li><strong>Consultation Credits:</strong> Additional consulting hours for future projects</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Policy Updates and Changes</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We reserve the right to modify this refund policy to reflect changes in our services, legal requirements, or business practices:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Policy changes will be posted on our website with an updated "Last updated" date</li>
              <li>Material changes will be communicated via email to active clients</li>
              <li>Changes will not affect ongoing projects unless mutually agreed upon</li>
              <li>Clients will have 30 days to review and discuss any concerns about policy changes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Legal Compliance and Jurisdiction</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              This refund policy is designed to comply with applicable consumer protection laws and regulations. 
              In cases where local laws provide greater consumer protections, those laws will take precedence over this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">12. Contact Information</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              For questions about our refund policy or to request a refund, please contact us through the following channels:
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">General Support</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Email:</strong> support@lunexa.dpdns.org<br />
                    <strong>Website:</strong> www.lunexa.com<br />
                    <strong>Response Time:</strong> Within 24 hours
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Refund Requests</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    <strong>Email:</strong> refunds@lunexa.dpdns.org<br />
                    <strong>Priority:</strong> High priority handling<br />
                    <strong>Response Time:</strong> Within 48 hours
                  </p>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <p className="text-gray-600 dark:text-gray-400">
                  <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM (Local Time)<br />
                  <strong>Emergency Support:</strong> Available for critical issues affecting live systems
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Refund;